//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';

import {
  CreatorCampaignRequestProductDetails,
  CreatorCampaignRequestProductVariantDimension,
} from '@apis/campaign-requests/creators/CreatorsCampaignRequestApiModel';
import {
  CampaignProductOrderEligibility,
  CampaignWorkflowProduct,
  ContentRightsSources,
  CreatorsCampaignWorkflowStep,
  CreatorsCampaignsMember,
  CreatorsCampaignsWorkflowResponse,
} from '@apis/creators-campaigns/CreatorsCampaignsApiModel';
import { CreatorsCampaignsOrderProductsDetails } from '@apis/creators-campaigns/creators-orders/CreatorOrdersApiModel';
import {
  CreatorCampaignProductStoreType,
  Product,
} from '@apis/creators-campaigns/creators-proposals/CreatorsProposalsApiModel';
import {
  CreatorsCampaignsExportPaymentsHistoryRequest,
  CreatorsCampaignsPaymentsHistoryRequest,
  CreatorsCampaignsPaymentsHistoryRequestFilters,
  PaymentsPayoutMethods,
  PaymentsPayoutMethodsType,
  PaymentsTransactionStatus,
  PaymentsTransactionStatusType,
} from '@apis/payments/paymentsApiModel';
import { ShopifyStoreInfoResponse } from '@apis/shopify/ShopifyApiModel';
import {
  CREATOR_CAMPAIGN_CONFIGURATION_BAZAARVOICE_CONTENT_RIGHTS,
  DEFAULT_ALL_SELECTION,
} from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignDetailsConstants';
import { CreatorsCampaignsTrackingIds } from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsTrackingId';
import {
  CampaignPlatformTask,
  CampaignWorkflowCreatorsTasks,
  CreatorBasicInfo,
  CreatorMinimalInfoInterface,
  CreatorsCampaignsPagination,
  CreatorsCampaignsPaymentCreator,
  CreatorsCampaignsPaymentHistoryFilters,
  UpdateCampaignBasicsFormValues,
  UpdateCampaignContentRightsFormValues,
} from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsTypes';
import { SummaryProduct } from '@campaigns/components/campaign-requests/creator-campaigns/campaign-configuration/summary/products-section/types/SummaryProduct';
import { isAffiliatesAndFixedPayComplete } from '@campaigns/components/campaign-requests/creator-campaigns/campaign-configuration/utils/CampaignAffiliateConfigurationUtils';
import {
  CreatorExportInfoType,
  CreatorPortalLinkType,
} from '@campaigns/components/campaign-requests/creator-campaigns/campaign-members/creators-link-warning-modal/CreatorsSensitiveLinkWarningModalTypes';
import { CreatorsCampaignsMemberMetadataForm } from '@campaigns/components/campaign-requests/creator-campaigns/campaigns-creators/CreatorDetails';
import {
  PRODUCT_IDENTIFIER_TITLE_WITH_ONLY_DESCRIPTION,
  PRODUCT_IDENTIFIER_TITLE_WITH_ONLY_PRODUCT_ID,
  PRODUCT_IDENTIFIER_TITLE_WITH_PRODUCT_ID_AND_DESCRIPTION,
  PRODUCT_IDENTIFIER_TITLE_WITH_PRODUCT_ID_AND_VARIANT_ID,
  SHOPIFY_SHOP_URL_DOMAIN_SUFFIX,
} from '@campaigns/components/campaigns-requests/creator-campaigns/CreatorsCampaignRequestDetailsConstants';
import { NETWORK_OPTIONS } from '@campaigns/constants/CampaignMediaUploadsConstants';
import { CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES } from '@campaigns/constants/CampaignRequestFormValidationConstants';
import {
  DATE_FORMAT_FULL,
  DATE_FORMAT_FULL_FILENAME,
} from '@campaigns/constants/DateConstants';
import { PrimaryRouteAnchor } from '@components/RouteAnchor';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { BadgeType } from '@components/atoms/badge/Badge';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import { ProfileNetwork } from '@influencers/constants/influencersConstants';
import { SocialCreatorsPlatforms } from '@models/CreatorsTypes';
import DateString from '@models/DateString';
import { SocialNetwork } from '@models/SocialNetwork';
import TimeZoneId from '@models/TimeZoneId';
import {
  formatDateWithTime,
  getRangeTimestampsFromAliasedDateRange,
  monthDayYearDateFormat,
} from '@utils/dateUtils';
import {
  isDefined,
  isEmptyValue,
  isTruthy,
  isUndefined,
} from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';
import { getUrlWithoutProtocol } from '@utils/urlUtils';
import { isValidUrlWithProtocol } from '@utils/urlValidation';

export const getCreatorsCampaignsStatus = (
  from: Date | undefined,
  to: Date | undefined,
) => {
  if (isUndefined(from)) {
    return {
      text: 'Scheduled',
      type: BadgeType.Focus,
    };
  }

  const todayDate = Date.now();
  if (from.getTime() > todayDate) {
    return {
      text: 'Scheduled',
      type: BadgeType.Secondary,
    };
  } else if ((to?.getTime() ?? Number.MAX_SAFE_INTEGER) < todayDate) {
    return {
      text: 'Completed',
      type: BadgeType.Success,
    };
  } else {
    return {
      text: 'In Progress',
      type: BadgeType.Warning,
    };
  }
};

export const getRelativeTime = (date: Date): string => {
  const minutesDifference = moment().diff(moment(date), 'minutes');

  if (minutesDifference < 1) {
    return 'Just now';
  }
  const hoursDifference = moment().diff(moment(date), 'hours');
  if (hoursDifference < 24) {
    return `${hoursDifference} hours ago`;
  }

  return moment(date).format('MMM DD, YYYY');
};

// Validations

export const cityRegExp = new RegExp(/^[a-zA-Z\s\-.'’]+$/);

export const allowedAlphabeticLettersInName =
  'A-Za-zÀ-ÿ\\u0100-\\u017F\\u0180-\\u024F';

export const allowedCharactersInName = `${allowedAlphabeticLettersInName}_' -`;

// This regex ensures there is at least one letter in the name while allowing some special characters such as "-" and "'"
export const nameRegExp = new RegExp(
  `^(?=.*[${allowedAlphabeticLettersInName}])[${allowedCharactersInName}]+$`,
);

const stateRegExp = new RegExp(/^[a-zA-Z]+$/);

const nonSpecialCharRegExp = new RegExp(/^[^\W]+$/);

const emailRegExp = new RegExp(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);

const phoneNumberRegExp = new RegExp(/^\s*\+?\s*[0-9]{7,}$/);

const validator = ({
  errorMessage,
  isMandatory = false,
  regExp,
  shouldValidate = true,
  value,
}: {
  errorMessage?: string;
  isMandatory?: boolean;
  regExp?: RegExp;
  shouldValidate?: boolean;
  value?: string;
}): { message: string | undefined } | undefined => {
  if (!shouldValidate) {
    return undefined;
  }
  if (isMandatory && !value) {
    return { message: errorMessage };
  }
  if (value && regExp && !regExp.test(value)) {
    return { message: errorMessage };
  }
  return undefined;
};

export const getCreatorDetailsMetadataFormErrors = ({
  affiliateDetails,
  email,
  firstName,
  isCreatorDetailsUsedForShipping = false,
  lastName,
  phone,
  shippingDetails = {},
}: CreatorsCampaignsMemberMetadataForm) => {
  // Determine if shipping details are mandatory
  const isShippingDetailsMandatory =
    Boolean(isCreatorDetailsUsedForShipping) ||
    Boolean(shippingDetails.firstName) ||
    Boolean(shippingDetails.lastName) ||
    Boolean(shippingDetails.phone) ||
    Boolean(shippingDetails.email) ||
    Boolean(shippingDetails.address?.city) ||
    Boolean(shippingDetails.address?.line1) ||
    Boolean(shippingDetails.address?.state) ||
    Boolean(shippingDetails.address?.country) ||
    Boolean(shippingDetails.address?.postalCode);

  const isCreatorDetailsMandatoryForShipping =
    Boolean(isCreatorDetailsUsedForShipping) && isShippingDetailsMandatory;

  const isShippingFieldsMandatory =
    !isCreatorDetailsMandatoryForShipping && isShippingDetailsMandatory;

  const validationResult = {
    // Creator contact details
    email: validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.EMAIL,
      isMandatory: isCreatorDetailsMandatoryForShipping,
      regExp: emailRegExp,
      value: email,
    }),
    firstName: validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.NAME,
      isMandatory: true, // First name is always mandatory
      regExp: nameRegExp,
      value: firstName,
    }),
    lastName: validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.NAME,
      isMandatory: isCreatorDetailsMandatoryForShipping,
      regExp: nameRegExp,
      value: lastName,
    }),
    phone: validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.PHONE,
      isMandatory: isCreatorDetailsMandatoryForShipping,
      regExp: phoneNumberRegExp,
      value: phone,
    }),
    // Shipping address details (always required when shipping details are mandatory)
    'shippingDetails.address.city': validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.CITY,
      isMandatory: isShippingDetailsMandatory,
      regExp: cityRegExp,
      shouldValidate: isShippingDetailsMandatory,
      value: shippingDetails.address?.city,
    }),
    'shippingDetails.address.country': validator({
      isMandatory: isShippingDetailsMandatory,
      shouldValidate: isShippingDetailsMandatory,
      value: shippingDetails.address?.country,
    }),
    'shippingDetails.address.line1': validator({
      isMandatory: isShippingDetailsMandatory,
      shouldValidate: isShippingDetailsMandatory,
      value: shippingDetails.address?.line1,
    }),
    'shippingDetails.address.postalCode': validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.POSTAL_CODE,
      isMandatory: isShippingDetailsMandatory,
      regExp: nonSpecialCharRegExp,
      shouldValidate: isShippingDetailsMandatory,
      value: shippingDetails.address?.postalCode,
    }),
    'shippingDetails.address.state': validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.STATE,
      isMandatory: isShippingDetailsMandatory,
      regExp: stateRegExp,
      shouldValidate: isShippingDetailsMandatory,
      value: shippingDetails.address?.state,
    }),
    // Shipping contact details
    'shippingDetails.email': validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.EMAIL,
      isMandatory: isShippingFieldsMandatory,
      regExp: emailRegExp,
      shouldValidate: isShippingFieldsMandatory,
      value: shippingDetails.email,
    }),
    'shippingDetails.firstName': validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.NAME,
      isMandatory: isShippingFieldsMandatory,
      regExp: nameRegExp,
      shouldValidate: isShippingFieldsMandatory,
      value: shippingDetails.firstName,
    }),
    'shippingDetails.lastName': validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.NAME,
      isMandatory: isShippingFieldsMandatory,
      regExp: nameRegExp,
      shouldValidate: isShippingFieldsMandatory,
      value: shippingDetails.lastName,
    }),
    'shippingDetails.phone': validator({
      errorMessage: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.PHONE,
      isMandatory: isShippingFieldsMandatory,
      regExp: phoneNumberRegExp,
      shouldValidate: isShippingFieldsMandatory,
      value: shippingDetails.phone,
    }),
    'affiliateDetails.affiliateLink': (() => {
      const affiliateLink = affiliateDetails?.affiliateLink;
      if (affiliateLink && !isValidUrlWithProtocol(affiliateLink)) {
        return { message: CREATOR_DETAILS_VALIDATION_ERROR_MESSAGES.URL };
      }
      return undefined;
    })(),
  };

  // Remove undefined values
  return JSON.parse(
    JSON.stringify(validationResult),
  ) as typeof validationResult;
};

export const formatCampaignDate = (
  date: Date | undefined,
  userTimeZone: TimeZoneId,
) => {
  if (!isDefined(date)) {
    return 'Present';
  }
  const dateString = DateString.fromTimestamp(date, userTimeZone);
  return DateString.format(dateString, monthDayYearDateFormat);
};

/**
 * Helper function to convert SocialNetwork type to SocialCreatorsPlatforms type
 * If network is facebook, we map it to Instagram as well
 * @param {SocialNetwork} network network to convert
 * @returns {SocialCreatorsPlatforms|undefined} returns undefined if unable to match to SocialCreatorsPlatforms type
 */
export const getCreatorsCampaignsPlatform = (
  network: SocialNetwork,
): SocialCreatorsPlatforms | undefined => {
  switch (network.toLowerCase()) {
    case SocialNetwork.Instagram.toLowerCase():
      return SocialNetwork.Instagram;
    case SocialNetwork.TikTok.toLowerCase():
      return SocialNetwork.TikTok;
    case SocialNetwork.Facebook.toLowerCase():
      return SocialNetwork.Instagram;
    case SocialNetwork.YouTube.toLowerCase():
      return SocialNetwork.YouTube;
    default:
      return undefined;
  }
};

export const mergeSocialTexts = (
  texts: string[] = [],
  startingSymbol?: string,
) => {
  if (!startingSymbol) {
    return texts.join(' ');
  }

  return texts
    .map((socialText) =>
      socialText.startsWith(startingSymbol)
        ? socialText
        : `${startingSymbol}${socialText}`,
    )
    .join(' ');
};

/**
 * Function to verify if at least 1 campaign workflow step is completed
 * This function should be invoked everytime we want to save a campaign configuration workflow
 */
export const isCampaignWorkflowComplete = (
  campaignWorkflow?: CreatorsCampaignsWorkflowResponse,
) => {
  if (!isDefined(campaignWorkflow)) {
    return false;
  }

  const stepChecks = [
    {
      step: CreatorsCampaignWorkflowStep.Products,
      check: () =>
        campaignWorkflow?.socoProductGift?.enabled &&
        isSocoProductGiftComplete(campaignWorkflow.socoProductGift),
    },
    {
      step: CreatorsCampaignWorkflowStep.ContentRights,
      check: () =>
        campaignWorkflow?.contentRights?.enabled &&
        isContentRightsComplete(campaignWorkflow.contentRights),
    },
    {
      step: CreatorsCampaignWorkflowStep.Payment,
      check: () =>
        (!!campaignWorkflow?.fixedPay?.enabled ||
          !!campaignWorkflow?.storeConfigurations?.pricingRule?.enabled ||
          !!campaignWorkflow?.googleAnalyticsConfiguration?.enabled ||
          !!campaignWorkflow?.impact?.enabled) &&
        isAffiliatesAndFixedPayComplete(
          campaignWorkflow.fixedPay,
          campaignWorkflow.googleAnalyticsConfiguration,
          {
            storeConfigurations: campaignWorkflow.storeConfigurations,
            influencersCommission: campaignWorkflow.influencersCommission,
          },
        ),
    },
    {
      step: CreatorsCampaignWorkflowStep.Tasks,
      check: () =>
        campaignWorkflow?.socoInfluencerTasks?.enabled &&
        isSocoInfluencerTasksComplete(campaignWorkflow.socoInfluencerTasks),
    },
  ];

  return stepChecks.some((check) => check.check());
};

export const isCampaignBriefComplete = (
  campaignBrief: UpdateCampaignBasicsFormValues,
) => {
  return !!campaignBrief.title && !!campaignBrief.campaignImageUrl;
};

export const isSocoProductGiftComplete = (
  socoProductGift: CreatorsCampaignsWorkflowResponse['socoProductGift'],
) => {
  if (!socoProductGift?.enabled) {
    return true;
  }
  const hasProducts = !isEmpty(socoProductGift.products);
  const isMaxCostOfProductsSettingValid = isDefined(
    socoProductGift.maxCostOfProducts,
  )
    ? (socoProductGift.maxCostOfProducts ?? -1) > 0
    : true;
  const isMaxNumberOfProductsSettingValid = isDefined(
    socoProductGift.maxNumberOfProducts,
  )
    ? (socoProductGift.maxNumberOfProducts ?? -1) > 0
    : true;
  const isNumberOfProductsSettingValid = isDefined(
    socoProductGift.numberOfProducts,
  )
    ? (socoProductGift.numberOfProducts ?? -1) > 0
    : true;
  return (
    hasProducts &&
    isMaxCostOfProductsSettingValid &&
    isMaxNumberOfProductsSettingValid &&
    isNumberOfProductsSettingValid
  );
};

export const isContentRightsComplete = (
  contentRights: CreatorsCampaignsWorkflowResponse['contentRights'],
) => {
  if (!contentRights?.enabled) {
    return true;
  }

  const { expiryDate } = contentRights;
  if (isDefined(expiryDate) && moment(expiryDate).isBefore(moment.now())) {
    return false;
  }

  return !!contentRights.description;
};

export const isSocoInfluencerTasksEmpty = (
  socoInfluencerTasks: CampaignWorkflowCreatorsTasks,
): boolean => {
  const instagramTasks = socoInfluencerTasks.tasks?.instagramTasks;
  const tiktokTasks = socoInfluencerTasks.tasks?.tiktokTasks?.videos;
  const youtubeTasks = socoInfluencerTasks.tasks?.youtubeTasks?.videos;

  const isEmptyInstagramTask =
    !instagramTasks || Object.keys(instagramTasks).length === 0;
  const isEmptyTikTokTask = !tiktokTasks || tiktokTasks.length === 0;
  const isEmptyYouTubeTask = !youtubeTasks || youtubeTasks.length === 0;

  return isEmptyInstagramTask && isEmptyTikTokTask && isEmptyYouTubeTask;
};

export const isSocoInfluencerTasksComplete = (
  socoInfluencerTasks: CreatorsCampaignsWorkflowResponse['socoInfluencerTasks'],
) => {
  const areTasksValid = (tasks: CampaignPlatformTask[] = []) =>
    !tasks.some((task) => !task.title || !task.guidance);

  const instagramTasks = socoInfluencerTasks?.tasks?.instagramTasks;
  const instagramTasksList = instagramTasks
    ? Object.values(instagramTasks).filter(isDefined)
    : [];

  return (
    areTasksValid(instagramTasksList) &&
    areTasksValid(socoInfluencerTasks?.tasks?.tiktokTasks?.videos) &&
    areTasksValid(socoInfluencerTasks?.tasks?.youtubeTasks?.videos)
  );
};

export const formatContentRightsFromFormInput = (
  contentRights: UpdateCampaignContentRightsFormValues,
): CreatorsCampaignsWorkflowResponse['contentRights'] => {
  const { description, expiryDate, isEnabled, source } = contentRights;
  return isEnabled
    ? {
        description:
          source === ContentRightsSources.CUSTOM
            ? description
            : CREATOR_CAMPAIGN_CONFIGURATION_BAZAARVOICE_CONTENT_RIGHTS,
        enabled: true,
        expiryDate:
          source === ContentRightsSources.CUSTOM && isTruthy(expiryDate)
            ? new Date(expiryDate)
            : undefined,
        source,
      }
    : {};
};

export const getDisabledNoEcomAlertContent = (
  isGAConnected?: boolean,
  isEcomEnabled?: boolean,
  hasGAReservedParam?: boolean,
) => {
  if (isGAConnected) {
    if (!isEcomEnabled) {
      return (
        <>
          Affiliate configuration is not available unless you enable the
          ecommerce setting for Google Analytics.{' '}
          <PrimaryAnchor
            href={
              'https://support.bazaarvoice.com/s/article/Supported-Analytics-Providers-at-Curalate'
            }
            trackingId={
              CreatorsCampaignsTrackingIds.CONFIGURATION_PAYMENT_ECOMMERCE_NOT_ENABLED_ALERT_LINK
            }
          >
            Learn More
          </PrimaryAnchor>
          .
        </>
      );
    } else if (!hasGAReservedParam) {
      return (
        <>
          Affiliate configuration is not available unless you configure a
          reserved Google Analytics tracking parameter. Visit the{' '}
          <PrimaryRouteAnchor
            state='account-settings.tracking'
            trackingId={
              CreatorsCampaignsTrackingIds.CONFIGURATION_PAYMENT_GA_RESERVED_PARAMETER_NOT_CONFIGURED_ALERT_LINK
            }
          >
            Settings page
          </PrimaryRouteAnchor>{' '}
          to set one.
        </>
      );
    }
  }

  return (
    <>
      Affiliate configuration is not available unless you connect an analytics
      provider. Visit the{' '}
      <PrimaryRouteAnchor
        state='account-settings.analytics'
        trackingId={
          CreatorsCampaignsTrackingIds.CONFIGURATION_PAYMENT_GA_NOT_ENABLED_ALERT_LINK
        }
      >
        Settings page
      </PrimaryRouteAnchor>{' '}
      to connect.
    </>
  );
};

/**
 * Extracts the product store source label from a PDP URL.
 *
 * @param pdpUrl - The URL of the product details page. Can be undefined.
 * @param storeType - The type of store as a `CreatorCampaignProductStoreType`.
 * @returns The extracted label or short domain , returns a default empty value if the URL is invalid or undefined.
 */
export const extractProductStoreSourceLabel = ({
  pdpUrl,
  storeInfo,
  storeType,
}: {
  pdpUrl?: string;
  storeType?: CreatorCampaignProductStoreType;
  storeInfo?: ShopifyStoreInfoResponse;
}): string => {
  try {
    if (!pdpUrl) {
      throw new Error();
    }
    // Remove protocol (http, https) and "www."
    const strippedUrl = getUrlWithoutProtocol(pdpUrl, true);

    // Extract the domain before any slashes
    const domain = strippedUrl.split('/')[0];
    if (!domain) {
      throw new Error();
    }

    // Helper function to check if a product page URL belongs to the connected Shopify store
    const isProductFromConnectedStore = (
      pdpUrl: string,
      shopUrl: string,
      shopCustomDomains: string[] | undefined,
    ): boolean => {
      const shopUrls = [shopUrl, ...(shopCustomDomains ?? [])];
      return shopUrls.some((url) => pdpUrl.includes(url));
    };

    // Process Shopify store URLs if storeType is SHOPIFY
    if (storeType === CreatorCampaignProductStoreType.SHOPIFY) {
      if (
        storeInfo?.appName &&
        storeInfo.shopUrl &&
        isProductFromConnectedStore(
          pdpUrl,
          storeInfo.shopUrl,
          storeInfo.customDomains,
        )
      ) {
        return storeInfo.appName;
      } else {
        return domain.replace(SHOPIFY_SHOP_URL_DOMAIN_SUFFIX, '');
      }
    }
    return domain;
  } catch {
    return DEFAULT_EMPTY_VALUE;
  }
};

export function formatDate(date: Date, timezone: TimeZoneId) {
  const formattedDate = moment
    .tz(date, timezone)
    .format(DATE_FORMAT_FULL)
    .toString();
  return formattedDate;
}

/**
 * extract variant dimensions as a concatinated string value joined by " - "
 *
 * @param {CreatorCampaignRequestProductVariantDimension[] | undefined} dimensions
 * @returns {string}
 */
const extractJoinedVariantDimensionValues = (
  dimensions: CreatorCampaignRequestProductVariantDimension[] | undefined,
): string => {
  if (!dimensions?.length) {
    return '';
  }

  return dimensions
    .map((d) => d.value?.trim())
    .filter(Boolean)
    .join(' - ');
};

/**
 * * Determines the product identifier based on the available fields.
 * It returns an object containing:
 * - `productIdentifier`: A formatted identifier string.
 * - `productIdentifierTitle`: An informational title describing the format.
 *
 * @param {Product | CreatorsCampaignsOrderProductsDetails} product
 * @returns {{ productIdentifier: string; productIdentifierTitle: string }} An object with the formatted product identifier and its title.
 */
export const getCreatorSelectedProductIdentifier = (
  product: Product | CreatorsCampaignsOrderProductsDetails,
) => {
  if (
    !isEmptyValue(product.displayProductId) &&
    product.variantDimensions?.length
  ) {
    return {
      productIdentifier: `${product.displayProductId} / ${extractJoinedVariantDimensionValues(product.variantDimensions)}`,
      productIdentifierTitle:
        PRODUCT_IDENTIFIER_TITLE_WITH_PRODUCT_ID_AND_DESCRIPTION,
    };
  }
  if (
    !isEmptyValue(product.displayProductId) &&
    !isEmptyValue(product.externalVariantId)
  ) {
    return {
      productIdentifier: `${product.displayProductId} / ${product.externalVariantId}`,
      productIdentifierTitle:
        PRODUCT_IDENTIFIER_TITLE_WITH_PRODUCT_ID_AND_VARIANT_ID,
    };
  }
  if (
    !isEmptyValue(product.displayProductId) &&
    !isEmptyValue(product.description)
  ) {
    return {
      productIdentifier: `${product.displayProductId} / ${product.description}`,
      productIdentifierTitle:
        PRODUCT_IDENTIFIER_TITLE_WITH_PRODUCT_ID_AND_DESCRIPTION,
    };
  }
  if (!isEmptyValue(product.description)) {
    return {
      productIdentifier: product.description,
      productIdentifierTitle: PRODUCT_IDENTIFIER_TITLE_WITH_ONLY_DESCRIPTION,
    };
  }
  return {
    productIdentifier: product.displayProductId ?? DEFAULT_EMPTY_VALUE,
    productIdentifierTitle: PRODUCT_IDENTIFIER_TITLE_WITH_ONLY_PRODUCT_ID,
  };
};

/**
 * Generates a subtitle for a campaign product row, combining the display product ID and the number of variants.
 *
 * @param {CampaignWorkflowProduct | CreatorCampaignRequestProductDetails | SummaryProduct} product
 * @returns {string} The generated subtitle string, combining the display product ID (if available) and the number of variants (if available).
 */
export const getCreatorsCampaignProductRowSubtitle = (
  product:
    | CampaignWorkflowProduct
    | CreatorCampaignRequestProductDetails
    | SummaryProduct,
): string => {
  const productId = product.displayProductId ?? '';
  const variantsText = product.variants?.length
    ? `${product.variants.length} variants`
    : '';

  return [productId, variantsText].filter(Boolean).join(' / ');
};

/**
 * Returns the badge type based on the campaign product order eligibility status.
 *
 * @param {CampaignProductOrderEligibility} status
 * @returns {BadgeType} The correct badge type, with a neutral fallback
 */
export const getCampaignProductOrderEligibilityBadgeType = (
  status: CampaignProductOrderEligibility | undefined,
): BadgeType => {
  switch (status) {
    case CampaignProductOrderEligibility.ELIGIBLE:
      return BadgeType.Secondary;
    case CampaignProductOrderEligibility.INELIGIBLE:
      return BadgeType.Neutral;
    default:
      return BadgeType.Neutral;
  }
};

export const convertCreatorsCampaignsMemberInfoToCreatorBasicInfo = (
  creators: Partial<CreatorsCampaignsMember>[],
): CreatorBasicInfo[] => {
  if (!isDefined(creators)) {
    return [];
  }

  return creators
    .map((creator) => {
      const creatorInfo = creator.creator;
      const creatorProfile = creator.creatorPortalProfile;

      if (!creatorInfo) {
        return undefined;
      }
      return {
        creatorId: creatorInfo.id,
        email: creator.creatorMetadata?.email,
        name: creatorInfo.name,
        network: creatorInfo.network,
        profilePictureUrl: creatorInfo.profilePictureUrl,
        username: creatorInfo.username,
        creatorProfileId: creatorProfile?.id,
      };
    })
    .filter(isDefined);
};

export const convertCreatorsCampaignAffiliateListCreatorToCreatorBasicInfo = (
  creators: CreatorsCampaignsPaymentCreator[],
): CreatorBasicInfo[] => {
  if (!isDefined(creators)) {
    return [];
  }

  return creators.map(
    ({
      creatorId,
      creatorPortalProfile,
      email,
      name,
      network,
      profilePictureUrl,
      username,
    }) => {
      const creatorProfileId = creatorPortalProfile?.id;
      return {
        creatorId,
        email,
        name,
        network,
        profilePictureUrl,
        username,
        creatorProfileId,
      };
    },
  );
};

/**
 * Validates that the input string is a valid payment amount.
 * The amount should be positive and must not contain more than two decimal digits.
 */
export const isValidPaymentAmountFormat = (value: string): boolean => {
  const validAmountRegex = /^\d+(?:\.\d{0,2})?$/;
  return validAmountRegex.test(value);
};

/**
 * Returns the appropriate user-friendly payment method text for display given a transaction's paymentMethod
 */
export const getCampaignsPaymentMethodText = (
  payoutMethod: PaymentsPayoutMethodsType | undefined,
): string => {
  if (isDefined(payoutMethod)) {
    return payoutMethod === PaymentsPayoutMethods.BANK_TRANSFER
      ? 'Campaigns Budget'
      : 'Offline Payment';
  }
  return DEFAULT_EMPTY_VALUE;
};

/**
 * Returns the appropriate badge for display given a transaction's status
 */
export const getPaymentTransactionBadgeType = (
  status: PaymentsTransactionStatusType | undefined,
): BadgeType => {
  switch (status) {
    case PaymentsTransactionStatus.PROCESSING:
      return BadgeType.Warning;
    case PaymentsTransactionStatus.FAILED:
      return BadgeType.Error;
    case PaymentsTransactionStatus.PAID:
      return BadgeType.Success;
    default:
      return BadgeType.Neutral;
  }
};

const parseFailureMessage = (failureMessage: string): string => {
  try {
    const parsed = JSON.parse(failureMessage);
    return parsed?.errors?.[0]?.message ?? 'Payment could not be completed';
  } catch {
    return failureMessage;
  }
};

/**
 * Returns an additional info depending on the transactions paymentMethod and status
 */
export const constructPaymentTransactionStatusMessage = ({
  failureMessage,
  payoutMethod,
  status,
  updatedAt,
  userTimeZone,
}: {
  failureMessage?: string;
  payoutMethod?: PaymentsPayoutMethodsType;
  status?: PaymentsTransactionStatusType;
  updatedAt?: Date;
  userTimeZone: TimeZoneId;
}): string => {
  if (status === PaymentsTransactionStatus.PAID) {
    if (
      payoutMethod === PaymentsPayoutMethods.BANK_TRANSFER &&
      isDefined(updatedAt)
    ) {
      return `Deposited on ${formatDateWithTime(
        updatedAt.getTime(),
        userTimeZone,
        monthDayYearDateFormat,
      )}`;
    }

    if (payoutMethod === PaymentsPayoutMethods.OFFLINE) {
      return 'Marked as paid';
    }
  }

  if (status === PaymentsTransactionStatus.PROCESSING) {
    return 'Payment is being processed';
  }

  if (status === PaymentsTransactionStatus.FAILED) {
    if (!isEmptyValue(failureMessage)) {
      return parseFailureMessage(failureMessage);
    }
    return 'Payment could not be completed';
  }

  return DEFAULT_EMPTY_VALUE;
};

export const mapToSelectedCreatorsPayments = (
  selectedCreators: CreatorBasicInfo[],
  creatorsPaymentsMap: Record<string, CreatorsCampaignsPaymentCreator>,
) => {
  return Object.values(creatorsPaymentsMap).filter((creatorPayment) => {
    const selectedCreatorsIdsSet = new Set(
      selectedCreators.map(({ creatorId }) => creatorId),
    );
    return (
      creatorPayment.creatorId &&
      selectedCreatorsIdsSet.has(creatorPayment.creatorId)
    );
  });
};

/**
 * Returns an api export request object from campaign payments history params
 */
export const mapPaymentsHistoryFilterToExportRequest = ({
  campaignSlugName,
  filters,
}: {
  filters: CreatorsCampaignsPaymentHistoryFilters;
  campaignSlugName: string;
}): CreatorsCampaignsExportPaymentsHistoryRequest => {
  const { endTimestamp, startTimestamp } =
    getRangeTimestampsFromAliasedDateRange(filters.dateRange, true);

  const reqFilters: CreatorsCampaignsPaymentsHistoryRequestFilters = {
    searchTerm: filters.searchTerm,
    startDate: startTimestamp,
    endDate: endTimestamp,
  };

  if (filters.networks !== DEFAULT_ALL_SELECTION) {
    reqFilters.networks = filters.networks;
  }

  if (filters.statuses !== DEFAULT_ALL_SELECTION) {
    reqFilters.statuses = filters.statuses;
  }

  if (filters.payoutMethods !== DEFAULT_ALL_SELECTION) {
    reqFilters.payoutMethods = filters.payoutMethods;
  }

  return {
    campaignSlugName,
    filters: reqFilters,
  };
};

/**
 * Returns an api request object from campaign payments history params
 */
export const mapPaymentsHistoryFilterToRequest = ({
  campaignSlugName,
  filters,
  paginate,
}: {
  filters: CreatorsCampaignsPaymentHistoryFilters;
  paginate: CreatorsCampaignsPagination;
  campaignSlugName: string;
}): CreatorsCampaignsPaymentsHistoryRequest => {
  return {
    ...mapPaymentsHistoryFilterToExportRequest({
      campaignSlugName,
      filters,
    }),
    paginate: {
      page: paginate.pageNumber,
      pageSize: paginate.pageSize,
      disable: false,
    },
  };
};

export const generateCampaignPaymentHistoryCsvFileName = (
  campaignSlugName: string | undefined,
  timeZone: TimeZoneId,
): string => {
  const formattedTimestamp = moment
    .tz(new Date(), timeZone)
    .format(DATE_FORMAT_FULL_FILENAME);
  return `${campaignSlugName ?? 'campaign'}_payments_history_${formattedTimestamp}.csv`;
};

/** Returns the network list given the platform filter */
export const getNetworkListFromPlatformFilter = (
  platformFilter: SocialCreatorsPlatforms | typeof DEFAULT_ALL_SELECTION,
): SocialCreatorsPlatforms[] => {
  if (platformFilter === DEFAULT_ALL_SELECTION) {
    return [
      SocialNetwork.Instagram,
      SocialNetwork.TikTok,
      SocialNetwork.YouTube,
    ];
  } else {
    return [platformFilter];
  }
};

/**
 * Extracts media codes from social media URLs for different platforms
 * @param url - The social media URL to extract the code from
 * @param network - The social network platform
 * @returns The extracted media code or the original URL if extraction fails
 */
export const extractMediaCodeFromUrl = (
  url: string,
  network: ProfileNetwork,
): string => {
  const trimmedUrl = url.trim();

  switch (network) {
    case SocialNetwork.Instagram: {
      // Extract from URLs like: www.instagram.com/p/ABC123/ or www.instagram.com/reel/ABC123/
      const instagramMatch = /instagram\.com\/(?:p|reel|tv)\/([^/?]+)/.exec(
        trimmedUrl,
      );
      return instagramMatch?.[1] ?? trimmedUrl;
    }

    case SocialNetwork.YouTube: {
      // Extract from URLs like: www.youtube.com/watch?v=ABC123 or youtu.be/ABC123
      const youtubeMatch =
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([^&?/]+)/.exec(
          trimmedUrl,
        );
      return youtubeMatch?.[1] ?? trimmedUrl;
    }

    case SocialNetwork.TikTok: {
      // Extract from URLs like: www.tiktok.com/@user/video/123456
      const tiktokMatch =
        /tiktok\.com\/@[^/]+\/video\/(\d+)|vm\.tiktok\.com\/([^/?]+)/.exec(
          trimmedUrl,
        );
      return tiktokMatch?.[1] ?? tiktokMatch?.[2] ?? trimmedUrl;
    }

    default:
      return trimmedUrl;
  }
};

/**
 * Validates a username string to ensure it's not empty
 * @param value - The username to validate
 * @returns Error message if invalid, empty string if valid
 */
export const validateUsername = (value: string): string => {
  if (!value.trim()) {
    return 'Username is required';
  }
  return '';
};

/**
 * Validates media URLs for a specific social network platform
 * @param value - The media URLs string (can contain multiple URLs separated by newlines)
 * @param network - The social network platform to validate against
 * @returns Error message if invalid, empty string if valid
 */
export const validateMediaUrls = (
  value: string,
  network: ProfileNetwork,
): string => {
  if (!value.trim()) {
    return 'Media URLs are required';
  }

  const urlList = value.split('\n').filter((url) => url.trim());
  const invalidUrls: string[] = [];

  urlList.forEach((url) => {
    const trimmedUrl = url.trim();
    let isValid = false;

    switch (network) {
      case SocialNetwork.Instagram: {
        // Match Instagram URLs and validate media code format
        // Media code pattern: alphanumeric, hyphens, underscores (typically 11 chars)
        isValid =
          /instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]{11})(?:\/|\?|$)/.test(
            trimmedUrl,
          );
        break;
      }

      case SocialNetwork.YouTube: {
        // Match YouTube URLs and validate video ID format
        // Video ID pattern: alphanumeric, hyphens, underscores (typically 11 chars)
        const youtubePatterns = [
          /youtube\.com\/watch\?v=([A-Za-z0-9_-]{10,12})(?:[&#?]|$)/,
          /youtu\.be\/([A-Za-z0-9_-]{10,12})(?:[&#?]|$)/,
          /youtube\.com\/shorts\/([A-Za-z0-9_-]{10,12})(?:[&#?]|$)/,
        ];
        isValid = youtubePatterns.some((pattern) => pattern.test(trimmedUrl));
        break;
      }

      case SocialNetwork.TikTok: {
        // Match TikTok URLs and validate video ID format
        // Video ID pattern: numeric digits (typically 19 digits)
        const tiktokPatterns = [
          /tiktok\.com\/@[^/]+\/video\/(\d{19})(?:\/.*)?(?:\?.*)?$/,
          /vm\.tiktok\.com\/([A-Za-z0-9]{8,12})(?:\/.*)?(?:\?.*)?$/,
        ];
        isValid = tiktokPatterns.some((pattern) => pattern.test(trimmedUrl));
        break;
      }

      default:
        isValid = true;
    }

    if (!isValid) {
      invalidUrls.push(trimmedUrl);
    }
  });

  if (invalidUrls.length > 0) {
    const networkName =
      NETWORK_OPTIONS.find((option) => option.value === network)?.label ??
      network;
    return `Invalid URL format for ${networkName}. Please check: ${invalidUrls.slice(0, 2).join(', ')}${invalidUrls.length > 2 ? '...' : ''}`;
  }
  return '';
};

/*
 * Returns number of creators by reading number of rows in a csv from a blob, it can return undefined if there are any issues
 * @param data a blob which is a csv file
 * @returns number of creators | undefined
 */
export const getNumberOfCreatorsFromExportedCSV = async (
  data: Blob | undefined,
): Promise<number | undefined> => {
  let numberOfCreators: number | undefined;
  try {
    if (isUndefined(data)) {
      return;
    }
    // Get the number of rows in the CSV file
    const csvText = await data.text();
    const rows = csvText.split('\n');
    numberOfCreators = rows.length - 1; // Subtract 1 to exclude the header row
  } catch {
    // swallow error
  }
  return numberOfCreators;
};

/**
 * Returns a grammatically correct and capitalized string from the creator link's type
 * @param type CreatorPortalLinkType | CreatorExportInfoType
 * @param isPlural false by default. pluralizes the string
 * @returns string
 */
export const getReadableCreatorLinkType = (
  type: CreatorPortalLinkType | CreatorExportInfoType,
  isPlural = false,
) => {
  switch (type) {
    case CreatorPortalLinkType.CAMPAIGN_LINK:
      return pluralize(isPlural ? 0 : 1, 'Campaign Link', undefined, false);
    case CreatorPortalLinkType.PAYMENT_LINK:
      return pluralize(isPlural ? 0 : 1, 'Payment Link', undefined, false);
    case CreatorPortalLinkType.CONTENT_REVIEW_LINK:
      return pluralize(
        isPlural ? 0 : 1,
        'Content Review Link',
        undefined,
        false,
      );
    case CreatorExportInfoType.CREATORS_DETAILS:
      return pluralize(
        isPlural ? 2 : 1,
        "Creator's Details",
        "Creators' Details",
        false,
      );
    default:
      return '';
  }
};

/**
 * Validates that the selected creator's platform matches the selected network
 * @param creator - The selected creator
 * @param selectedNetwork - The selected social network platform
 * @returns Error message if platforms don't match, null if valid
 */
export const validateCreatorPlatform = (
  creator: CreatorMinimalInfoInterface | null,
  selectedNetwork: ProfileNetwork,
): string | null => {
  if (creator && creator.platform !== selectedNetwork) {
    return `Selected creator is not on ${selectedNetwork}`;
  }
  return null;
};
